import { lazy, type ComponentType } from 'react';
import type { Permission } from '@blackblack/shared';
import { FeedPage } from '../features/feed/FeedPage';
import { DiscoverPage } from '../features/discover/DiscoverPage';
import { MusicPage } from '../features/music/MusicPage';

// Rarely-first-visited screens are code-split.
const SearchPage = lazy(() => import('../features/discover/SearchPage').then((m) => ({ default: m.SearchPage })));
const HashtagPage = lazy(() => import('../features/discover/HashtagPage').then((m) => ({ default: m.HashtagPage })));
const VideoPage = lazy(() => import('../features/feed/VideoPage').then((m) => ({ default: m.VideoPage })));
const TrackPage = lazy(() => import('../features/music/TrackPage').then((m) => ({ default: m.TrackPage })));
const ArtistPage = lazy(() => import('../features/music/ArtistPage').then((m) => ({ default: m.ArtistPage })));
const AlbumPage = lazy(() => import('../features/music/AlbumPage').then((m) => ({ default: m.AlbumPage })));
const LikedSongsPage = lazy(() => import('../features/music/LikedSongsPage').then((m) => ({ default: m.LikedSongsPage })));
const PlaylistPage = lazy(() => import('../features/playlists/PlaylistPage').then((m) => ({ default: m.PlaylistPage })));
const ProfilePage = lazy(() => import('../features/profile/ProfilePage').then((m) => ({ default: m.ProfilePage })));
const MyProfileRedirect = lazy(() => import('../features/profile/ProfilePage').then((m) => ({ default: m.MyProfileRedirect })));
const FollowListPage = lazy(() => import('../features/profile/FollowListPage').then((m) => ({ default: m.FollowListPage })));
const EditProfilePage = lazy(() => import('../features/profile/EditProfilePage').then((m) => ({ default: m.EditProfilePage })));
const SettingsPage = lazy(() => import('../features/profile/SettingsPage').then((m) => ({ default: m.SettingsPage })));
const NotificationsPage = lazy(() => import('../features/notifications/NotificationsPage').then((m) => ({ default: m.NotificationsPage })));
const CreatePage = lazy(() => import('../features/create/CreatePage').then((m) => ({ default: m.CreatePage })));
const LoginPage = lazy(() => import('../features/auth/AuthPages').then((m) => ({ default: m.LoginPage })));
const RegisterPage = lazy(() => import('../features/auth/AuthPages').then((m) => ({ default: m.RegisterPage })));
const ForgotPasswordPage = lazy(() => import('../features/auth/AuthPages').then((m) => ({ default: m.ForgotPasswordPage })));
const ResetPasswordPage = lazy(() => import('../features/auth/AuthPages').then((m) => ({ default: m.ResetPasswordPage })));
const BillingPage = lazy(() => import('../features/billing/BillingPage').then((m) => ({ default: m.BillingPage })));
const TermsPage = lazy(() => import('../features/legal/LegalPages').then((m) => ({ default: m.TermsPage })));
const PrivacyPage = lazy(() => import('../features/legal/LegalPages').then((m) => ({ default: m.PrivacyPage })));
const AdminPage = lazy(() => import('../features/admin/AdminPage').then((m) => ({ default: m.AdminPage })));

export interface RouteDef {
  path: string;
  component: ComponentType;
  /** Keep mounted when navigating away (tab roots keep scroll & feed position). */
  keepAlive?: boolean;
  /** 'immersive' = full-bleed video UI, 'focus' = no bottom nav (flows like create/login). */
  chrome?: 'default' | 'immersive' | 'focus';
  auth?: boolean;
  permission?: Permission;
}

export const ROUTES: RouteDef[] = [
  { path: '/', component: FeedPage, keepAlive: true, chrome: 'immersive' },
  { path: '/discover', component: DiscoverPage, keepAlive: true },
  { path: '/search', component: SearchPage },
  { path: '/tag/:tag', component: HashtagPage },
  { path: '/music', component: MusicPage, keepAlive: true },
  { path: '/library/liked', component: LikedSongsPage, auth: true },
  { path: '/sound/:id', component: TrackPage },
  { path: '/artist/:id', component: ArtistPage },
  { path: '/album/:id', component: AlbumPage },
  { path: '/playlist/:id', component: PlaylistPage },
  { path: '/v/:id', component: VideoPage, chrome: 'immersive' },
  { path: '/create', component: CreatePage, chrome: 'focus', auth: true },
  { path: '/me', component: MyProfileRedirect, auth: true },
  { path: '/u/:username', component: ProfilePage },
  { path: '/u/:username/:kind', component: FollowListPage },
  { path: '/notifications', component: NotificationsPage, auth: true },
  { path: '/settings', component: SettingsPage, auth: true },
  { path: '/settings/profile', component: EditProfilePage, auth: true },
  { path: '/settings/billing', component: BillingPage, auth: true },
  { path: '/login', component: LoginPage, chrome: 'focus' },
  { path: '/register', component: RegisterPage, chrome: 'focus' },
  { path: '/forgot-password', component: ForgotPasswordPage, chrome: 'focus' },
  { path: '/reset-password', component: ResetPasswordPage, chrome: 'focus' },
  { path: '/voorwaarden', component: TermsPage },
  { path: '/privacy', component: PrivacyPage },
  { path: '/admin', component: AdminPage, chrome: 'focus', auth: true, permission: 'admin.access' },
];
